import { CheckCircle2, Tv } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { endpoints } from '../services/api';
import { useSession } from '../store/session';
import { SignInForm } from './settings/AccountTab';
import { buttonClass, inputClass } from './settings/controls';

/** Phone side of pairing: /pair?code=XXXXXXXX (from the TV's QR code). */
export function PairPage() {
  const { load, loaded, user, config } = useSession();
  const [code, setCode] = useState(() => new URLSearchParams(window.location.search).get('code') ?? '');
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.documentElement.dataset.theme = 'dark';
    void load();
  }, [load]);

  const approve = async (e: FormEvent) => {
    e.preventDefault();
    setState('busy');
    setError(null);
    try {
      await endpoints.pairApprove(code);
      setState('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t work');
      setState('idle');
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-[var(--bg)]">
      <main className="mx-auto max-w-md px-6 py-12">
        <div className="mb-8 flex items-center gap-3">
          <Tv className="h-7 w-7 text-[var(--accent)]" aria-hidden />
          <h1 className="text-2xl font-semibold">Sign in your screen</h1>
        </div>

        {!loaded ? null : !config?.features.accounts ? (
          <p className="muted">Accounts aren’t switched on yet.</p>
        ) : state === 'done' ? (
          <div className="glass flex items-start gap-3 p-5">
            <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-green-400" aria-hidden />
            <div>
              <p className="font-medium">Done – your screen is signing in.</p>
              <p className="muted mt-1 text-sm">It will pick up your photos, calendar and settings in a few seconds.</p>
            </div>
          </div>
        ) : !user ? (
          <>
            <p className="muted mb-4 text-sm">First, sign in to your KCL Canvas account on this phone.</p>
            <SignInForm returnTo={`/pair${code ? `?code=${encodeURIComponent(code)}` : ''}`} />
          </>
        ) : (
          <form onSubmit={approve} className="space-y-4">
            <p className="text-sm">
              Signed in as <strong>{user.email}</strong>. Enter the code shown on your TV:
            </p>
            <input
              className={`${inputClass} text-center font-mono text-2xl uppercase tracking-[0.2em]`}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="XXXX-XXXX"
              autoCapitalize="characters"
              autoComplete="one-time-code"
              maxLength={12}
              aria-label="Code from your TV"
              required
            />
            {error && <p className="text-sm text-red-400">{error}</p>}
            <button className={`${buttonClass} w-full py-3`} disabled={state === 'busy'} type="submit">
              Sign in my screen
            </button>
            <p className="muted text-xs">Only approve codes shown on a screen you control.</p>
          </form>
        )}
        <a href="/" className="muted mt-10 block text-center text-xs hover:underline">
          Open KCL Canvas on this device instead
        </a>
      </main>
    </div>
  );
}
